import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { IDevice } from 'src/app/building-managment/building-unit-details/add-lock/lock-management-interfaces';
import { IBuilding } from 'src/app/building-managment/building-management-interfaces';
import { FloorManagementService } from 'src/app/floor-management/floor-management.service';
import { NotFoundError } from 'src/app/shared/errors/errors';
import { NotificationService } from 'src/app/shared/errors/notification.service';
import { getFullName } from 'src/app/shared/utils/full-name.util';
import { PopupProfile } from '../profile-management.service';
import { AssignFloor, AssignUnit } from '../profile-assign-unit/profile-assign-unit.component';

@Component({
  selector: 'app-profile-access-lock-list',
  templateUrl: './profile-access-lock-list.component.html',
  styleUrls: ['./profile-access-lock-list.component.scss'],
})
export class ProfileAccessLockListComponent implements OnInit, OnDestroy {
  @Input() profileDetails!: PopupProfile;
  @Input() unit!: AssignUnit;
  @Input() building?: IBuilding;
  @Input() floors: AssignFloor[] = [];
  @Input() selectedLocks: IDevice[] = [];
  @Output() selectedLocksChange = new EventEmitter<IDevice[]>();
  @Output() continueRequested = new EventEmitter<IDevice[]>();
  @Input() dialogRef?: { close: () => void };
  @Output() stepperChange = new EventEmitter<number>();
  locks: IDevice[] = [];
  isLoading = false;
  loadFailed = false;
  private destroyed = new Subject<void>();

  constructor(
    private floorService: FloorManagementService,
    private notificationService: NotificationService
  ) {}

  get userName(): string {
    const p = this.profileDetails;
    return getFullName(p?.firstName, p?.middleName, p?.lastName);
  }

  get locationLabel(): string {
    const floor = this.floors.find(item => item.floorId === this.unit?.floorId);
    return [floor?.floorName, this.building?.buildingName].filter(Boolean).join(', ');
  }

  get canContinue(): boolean {
    return !this.isLoading && !this.loadFailed && this.locks.some(lock => this.isSelected(lock));
  }

  isSelected(lock: IDevice): boolean {
    return !!lock.deviceId && this.selectedLocks.some(selected => selected.deviceId === lock.deviceId);
  }

  toggleLock(lock: IDevice): void {
    if (!lock.deviceId || this.isLoading || this.loadFailed) return;
    this.selectedLocks = this.isSelected(lock)
      ? this.selectedLocks.filter(selected => selected.deviceId !== lock.deviceId)
      : [...this.selectedLocks, lock];
    this.selectedLocksChange.emit(this.selectedLocks);
  }

  continue(): void {
    if (this.canContinue) {
      this.continueRequested.emit(this.locks.filter(lock => this.isSelected(lock)));
    }
  }

  ngOnInit(): void {
    this.loadLocks();
  }

  loadLocks(): void {
    if (this.isLoading || !this.unit?.unitId) return;
    this.isLoading = true;
    this.loadFailed = false;
    this.floorService.getDeviceByUnitId(this.unit.unitId, false, false)
      .pipe(takeUntil(this.destroyed), finalize(() => this.isLoading = false))
      .subscribe({
        next: locks => {
          this.locks = [...(locks || [])].sort((a, b) =>
            (a.deviceName || '').localeCompare(b.deviceName || '', undefined, { numeric: true }));
          this.selectedLocks = this.locks.filter(lock => this.isSelected(lock));
          this.selectedLocksChange.emit(this.selectedLocks);
        },
        error: (error: any) => {
          this.locks = [];
          if (!(error instanceof NotFoundError)) {
            this.loadFailed = true;
            this.notificationService.showError(error?.message);
          }
        },
      });
  }

  back(): void {
    this.stepperChange.emit(33);
  }

  close(): void {
    this.dialogRef?.close();
  }

  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
  }
}